import { useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { Paper, Group, Stack, Text, Button } from '@mantine/core'
import { IconUsers, IconPlayerPlay } from '@tabler/icons-react'
import { isAuthenticatedAtom } from '../../atoms/authAtoms'
import { useTeams } from '../../hooks/useTeam'

/**
 * Prominent steer toward team play. For these cohorts ~all intended play is with
 * a team, but the catalog and story pages otherwise present SOLO as the default
 * path — students were starting solo playthroughs by accident. This banner
 * surfaces any team the player is on that has an active story and routes them to
 * the team dashboard (the only door that tags the session as team play).
 *
 * @param {string} [storyId] - When set, only show the banner for a team whose
 *   active story matches this id (used on a story's detail page). Omit to show
 *   all of the player's teams with an active story (used on the catalog).
 */
export function TeamPlayBanner({ storyId }) {
  const navigate = useNavigate()
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const { data: myTeams = [] } = useTeams({ enabled: isAuthenticated })

  const activeTeams = myTeams.filter((t) => {
    if (!t.current_story_id) return false
    if (storyId) return t.current_story_id === storyId
    return true
  })

  if (activeTeams.length === 0) return null

  return (
    <Stack gap="sm">
      {activeTeams.map((team) => (
        <Paper
          key={team.id}
          p="md"
          radius="md"
          style={{
            background: 'rgba(0, 180, 160, 0.10)',
            border: '1px solid var(--mantine-color-teal-7)',
          }}
        >
          <Group justify="space-between" wrap="nowrap" gap="md">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <IconUsers size={22} color="var(--mantine-color-teal-4)" style={{ flexShrink: 0 }} />
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Text size="sm" fw={600} c="teal.2">
                  Your team {team.name} is playing together
                </Text>
                <Text size="xs" c="dimmed">
                  {team.current_story_name
                    ? `Continue "${team.current_story_name}" with your team so your work counts toward Team Analysis.`
                    : 'Continue with your team so your work counts toward Team Analysis.'}
                </Text>
              </Stack>
            </Group>
            <Button
              color="teal"
              leftSection={<IconPlayerPlay size={16} />}
              onClick={() => navigate(`/teams/${team.id}`)}
              style={{ flexShrink: 0 }}
            >
              Continue with team
            </Button>
          </Group>
        </Paper>
      ))}
    </Stack>
  )
}
